import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ADD_PROMOTIONS_RETVAL, PROMOTION_TYPE } from '../../providers/myria/app-variables';
import { CustomersProvider } from '../../providers/myria/customer-provider/customers-provider';
import { APP_CONFIG } from '../../environment';
import { RmtPopupComponent } from '../../components/rmt-popup/rmt-popup';
import { PriceCalculatorProvider } from '../../providers/price-calculator/price-calculator';
import { AnalyticsEventType, SegmentLocationNames } from '../../model/types/analytics.const';
import { LoggerEvent, LoggerProvider } from '../../providers/logger/logger';
import { SegmentProvider } from '../../providers/analytics/segment';
import { IPromotion } from '../../model/models/promotions.interface';
import { IOrder } from '../../model/models/order/order-interface';
import { IPriceCalculator } from 'model/models/price-calculator/price-calculator.interface';
import { ErrorCodeType } from '../../model/types/error-code-type';
import { IPromoEngineModel } from '../../model/models/promotions.interface';
import { PriceCalculatorMapper } from '../../providers/price-calculator/price-calculator.mapper';
import { HttpProvider } from '../http/http';
import { RmtSnackbarComponent } from '../../components/rmt-snackbar/rmt-snackbar';
import { RouteProvider } from '../../providers/route/route';
import { ResourceProvider } from '../resource/resource';
import { PromoEngineEffect, RAF_STAGED_INELIGIBLE_REGIONS } from '../../model/types/promotionobject-type';


@Injectable()
export class PromoProvider {

  isSessionOpen = false;
  checkPromoPromise;
  checkPromoFinished = false;
  lastCheckPromo;
  lastCheckLang;
  wasAutoApplyPromoMessageShown = false;
  referralPromoCode;

  constructor(private customersProvider: CustomersProvider,
              private translate: TranslateService,
              private httpProvider: HttpProvider,
              private routeProvider: RouteProvider,
              private PriceCalculatorProvider: PriceCalculatorProvider,
              private resourceProvider: ResourceProvider,
              private analytics: SegmentProvider) {
  }
   
  logout() {
    this.referralPromoCode = null;
    this.isSessionOpen = false;
    this.lastCheckPromo = null;
    this.lastCheckLang= null;
    this.clearCheckPromo();
  }

  clearCheckPromo() {
    this.checkPromoFinished = false;
    this.checkPromoPromise = null;
  }

  canApplyPromo(promo, order){
    return (!promo.isPromoEngine && order.promoCode !== promo.PromoCode) ||
    (promo.isPromoEngine && (promo.isAutoApply || order.promoEngineCode !== promo.PromoCode));
  }

  sendPromotionAppliedAnalyticsEvent(promo) {
    let isWinback = promo.IsReferral && promo.PromoId == PROMOTION_TYPE.WINBACK;
    let promoName = this.getPromoName(promo);

    this.analytics.trackEvent({
      eventName: AnalyticsEventType.PromoApplied, analyticsData: {
        location: SegmentLocationNames.SendMoney,
        promoId: promo.PromoId,
        promoName: promoName,
        promoNameToLower: promoName.toLowerCase(),
        referralCode: promo.IsReferral && !isWinback ? promoName : null,
        promoType: promo.IsReferral ? 'RAF' : null,
        newCustomerReferral: (promo.IsReferral && !isWinback) ,
        referralProgram: promo.IsReferral ? 'RAF' : null
      }
    });
  }


  // should only be used internally so that the checkPromise storage is correct
  protected async getPromoEnginePromotions(order: any, state = null, isFirstTransaction = null, populatePaymentMethods = true) : Promise<IPromoEngineModel> {

    let initSession:Boolean = true;
    try {
      if (this.isSessionOpen){
        initSession = false;
      }
      this.isSessionOpen = true;
      let firstTransactionQueryString = "";
      if(isFirstTransaction !== null){
        firstTransactionQueryString = '&isFirstTransaction=' + isFirstTransaction;
      }

      if (populatePaymentMethods && (!order.paymentMethods || (order.paymentMethods && order.paymentMethods.length === 0))) {
        order.paymentMethods = await this.resourceProvider.getPaymentMethods();
      }

      const apiRequest  = PriceCalculatorMapper.fromOrderToCalculateRequest(order);
      if (state)
        apiRequest.selections = { ...apiRequest.selections, stateTo: state };
      if (order.isAutoApplyPromo || !order.promoEngineCode)
        apiRequest.selections.promoCode = null; // don't send auto apply promos or FX promos since otherwise we get an invalid coupon error
      
      const promoObj = await this.httpProvider.post(`${APP_CONFIG.remittanceApiBaseUrl}${'moneytransfercalculator/checkpromos?initPromo='+ initSession+ firstTransactionQueryString}`, apiRequest);

      // if we got back a promo code but didn't pass any promo code it means it must be an auto apply promo
      if (promoObj && promoObj.code && !apiRequest.selections.promoCode)
        promoObj.isAutoApply = true;
      return promoObj;
    }
    catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.PageException, 'Failed to get Promotion Engine digital promotion');
    }
  }

  async getPromotions(isLiteUser = false) {
    try {
      const promosObj= isLiteUser ? await (await this.customersProvider.getLRUserPromotions()) : await this.customersProvider.getCustomerPromotions();
      promosObj.Promotions.forEach(promo => {
        if (promo.PromoId == PROMOTION_TYPE.CUSTOMERREFERRAL) {
          promo.title = this.translate.instant('page.activityPage.rAFActivityHeader') + ' ' + this.translate.instant('page.promotionsPage.promotionsZeroFeeLabel');
          promo.subtitle1 = this.translate.instant('page.promotionsPage.firstTransactionText');
        }
        else if(promo.PromoId == PROMOTION_TYPE.WINBACK && promo.IsReferral) {
          promo.title = this.translate.instant('page.activityPage.rAFActivityHeader') + ' ' + this.translate.instant('page.promotionsPage.promotionsZeroFeeLabel');
          promo.subtitle1 = this.translate.instant('page.promotionsPage.availablePromoRAFLabel');
        } 
        else {
          let date = new Date(promo.ExpirationDate);
          let months = this.translate.instant('global.myRia.jsonContent.MonthList').split(',');
          promo.title = this.translate.instant(promo.IsPercentageFee ? 'page.promotionsPage.promotionsPercentFeeLabel' : 'page.promotionsPage.promotionsGeneralFeeLabel').replace('{{fee}}', promo.Fee);
          promo.subtitle1 = this.translate.instant('page.promotionsPage.expiresText').replace('{{month}}', months[date.getMonth()]).replace('{{day}}', date.getDate());
        }
      });
      return promosObj.Promotions;
    }
    catch (ex) {
      // OM-19415: prevent stacking when the Promotions endpoint fails repeatedly
      // (e.g. 500 across concurrent callers / quick re-navigations). If an error
      // popup is already on screen, log the exception but don't queue another one.
      if (document.getElementsByClassName('popup-error').length > 0) {
        LoggerProvider.exception(this, ex, LoggerEvent.PageException, 'Suppressed duplicate promo error popup');
        return;
      }
      RmtPopupComponent.showExceptionWithLogTranslation(this, ex, "page.errorPage.promoError");
    }
  }

  async checkDigitalPromo(order, state = null): Promise<IPriceCalculator> {
    return new Promise<IPriceCalculator>(async (resolve, reject) => {
        try {
            const res = await this.PriceCalculatorProvider.calculateOrderTotals(this.resourceProvider, order, state);
            const isPromoValid = res.isPromoValid;
            if (isPromoValid) {
                this.sendPromoAnalyticsEvent(AnalyticsEventType.PromoApplied, {promoId: ''}, order);
                return resolve(res);
            }
            else 
            {
                order.promoCode = '';
                return reject(false);
            }
        }
        catch (ex) {
          order.promoCode = '';
          LoggerProvider.exception(this, ex, LoggerEvent.PageException, 'Failed to check digital promo code');

          if(ex.errorCode === ErrorCodeType.PromoCodeAmountOutOfRange || ex.errorCode === ErrorCodeType.PromoCodeNotApplicable) 
            return reject(ex);

          return reject(ex);
        }
    })
  }

  async addPromo(promo: string, order: IOrder, isLiteUser: boolean = false): Promise<IPromotion[]> {
    return new Promise<IPromotion[]>(async (resolve, reject) => {
        try {
            const promoValue = promo.trim();
            let result: any = null;
            if (isLiteUser)
            result = await this.customersProvider.addLRPromotion(promoValue);
            else if (order && order.countryTo)
            result = await this.customersProvider.addPromotion(promoValue, order.countryTo);
            else
            result = await this.customersProvider.addPromotion(promoValue, '');
            let retVal = result.RetVal;
            if (retVal == ADD_PROMOTIONS_RETVAL.SUCCESS) {
                const promos = await this.getPromotions(isLiteUser);
                resolve(promos);
            }
            else {
                reject(true);
            }
        }
        catch (ex) {
            LoggerProvider.exception(this, ex, LoggerEvent.PageException, 'Unable to check promo');
            this.sendPromoAnalyticsEvent(AnalyticsEventType.PromoFailed, {errorType:'promocodeError'}, order);
            reject(false);
        }
    })
  }

  sendPromoAnalyticsEvent(event: string, info: {}, order: IOrder) {

    let promoName = order.promoCode;

    if(info["promoName"]){
      promoName = info["promoName"];
    }
    
    this.analytics.trackEvent({
      eventName: event, analyticsData: {
        location: SegmentLocationNames.SendMoney,
        promoName: promoName,
        promoNameToLower: promoName ? promoName.toLowerCase() : '',
        ...info
      }
    });
  }

  checkPromoEnginePromotions(order, forceCheck = false, state = null, isFirstTransaction = null, populatePaymentMethods = true) {
    // Delay in currencyTo after new country selection
    if (!order.currencyFrom || 
        !order.paymentMethod || 
        !order.deliveryMethodInternal || 
        !order.currencyTo) {
      LoggerProvider.info(this, 'Skipping check promo engine because of incomplete data');
      this.clearCheckPromo();
      return null;
    }
    
    let checkPromo = this.PriceCalculatorProvider.getOrderHash(order);
    let checkLang = HttpProvider.currentLanguage;  
    checkPromo += '/code:' + order.promoEngineCode; // if a coupon code has been provided it needs to be part of the hash since it has an influence on the result
    checkPromo += '/state:' + state; // necessary because of calculate v2 including the state in the selections

    if (!forceCheck && this.checkPromoPromise && !this.checkPromoFinished) {
      LoggerProvider.info(this, 'Tried to check promo engine but no change');
      return this.checkPromoPromise;
    }
    this.lastCheckPromo = checkPromo;
    this.lastCheckLang = checkLang;

    LoggerProvider.info(this, 'Check promo engine');
    let result = {
      success: false,
      promo: null
    };
    this.checkPromoFinished = false;
    const checkPromoPromise = new Promise(async(resolve) => {
      try {  
        const promoObj = await this.getPromoEnginePromotions(order, state, isFirstTransaction, populatePaymentMethods);
        if (checkPromoPromise !== this.checkPromoPromise)
          return await this.checkPromoPromise;

        if (!promoObj){
          LoggerProvider.info(this, LoggerEvent.PageException, 'Promo engine without result');
        } else if (promoObj.category === 'ERROR') {
          result.promo = promoObj;
          LoggerProvider.warning(this, LoggerEvent.PageException, 'Promo engine error', promoObj);
        } else {
          LoggerProvider.info(this, 'Promo code received promotion ' + promoObj.code, promoObj);
          result.success = true;
          result.promo = promoObj;
        }
      } catch (ex) {
        LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Failed to validated Promotion Engine Promo Code');
      }
      resolve(result);
      this.checkPromoFinished = true;
    });
    this.checkPromoPromise = checkPromoPromise;
    return this.checkPromoPromise;
  }

  showModalPromoError(errorCodeType: string) {
    let title: string;
    let subtitle: string;

    if (errorCodeType === ErrorCodeType.PromoCodeNotValid) {
      title = this.translate.instant('page.promotionsPage.promoRemovedHeader');
      subtitle = this.translate.instant('page.promotionsPage.promoRemovedContent');
    }

    if (errorCodeType === ErrorCodeType.PromoCodeNotApplicable) {
      title = this.translate.instant('page.promotionsPage.promoNotAppliedHeader');
      subtitle = this.translate.instant('page.promotionsPage.promoNotAppliedContent');
    }

    this.routeProvider.openActionSheetNew(this.routeProvider.pages.ModalAlertPage, {
      isWaiting: false,
      imageWithCircle: true,
      image: "assets/icon/yellow-warning-icon.svg",
      title,
      subtitle,
      showPrimaryButton: true,
      primaryBtnText: this.translate.instant('global.okButton'),
    });
  }

  cleanPromo(order) {
    order.promoCode = undefined;
    order.promoEngineCode = undefined;
    order.PromoTransIdLocked = undefined;
    order.IsReferralPromo = undefined;
    order.actualFee = undefined;
    order.promoId = undefined;
    order.promoTitle = undefined;
    order.promoName = undefined;
    order.promoType = undefined;
    order.promoSourcePlatform = undefined;
    order.promoCode = undefined;
  }

  async applyPromoToOrder(promo, order) {
    if (promo.isPromoEngine)
      order.promoEngineCode = promo.PromoCode;
    else if (promo.PromoCode && promo.PromoCode.length != 0) {
      order.promoCode = promo.PromoCode;
    } else {
      if (order.PromoTransIdLocked) await this.customersProvider.releasePromotion(order.PromoTransIdLocked).toPromise();
      order.PromoTransIdLocked = promo.TransId;
    }
    order.promoTitle = this.getAnalyticsPromoTitle(promo);
    order.promoName = this.getPromoName(promo);
    order.promoId = promo.TransId;
    order.IsReferralPromo = promo.IsReferral;
    order.actualFee = order.customerFee;
    order.isAutoApplyPromo = promo.isAutoApply;
    order.promoType = promo.promoType;
    order.promoSourcePlatform = promo.promoSourcePlatform;
    // order.promoCode = promo.PromoCode; // We should not set this as we are setting it above based on condition 
  }

  showPromoCodeAdded(order, promo, promoEngineError) {
    if (order.isAutoApplyPromo && this.wasAutoApplyPromoMessageShown) {
      return;
    }
    const promoCode = (order.promoCode) ? order.promoCode : order.promoEngineCode;
    if(order.lastShownPromoCode === promoCode) {
      return;
    }

    if (promo && promo.PromoCode && order.isPromoValid && !promoEngineError) {
      order.lastShownPromoCode = promoCode;
      let message = "";
      if(this.isPromoTypeReferral(promo)){
        message = 'page.rAFPage.codeAppliedMessage';
      } else if(promo.isAutoApply){
        message = 'page.promotionsPage.autoPromoAddedSuccessMessage';
      } else {
        message = 'page.promotionsPage.promotionAddedSuccessMessage';
      }
      RmtSnackbarComponent.present(this.translate.instant(message));
      if (order.isAutoApplyPromo) {
        this.wasAutoApplyPromoMessageShown = true;
      }
    } else {
      order.lastShownPromoCode = undefined;
    }
  }

  getAnalyticsPromoTitle(promo) {
    let title;
    if (promo.IsReferral && promo.PromoId == PROMOTION_TYPE.WINBACK) {
      title = promo.PromoName;
    } else {
      title = promo.PromoTitle ? promo.PromoTitle : this.getPromoName(promo);
    }
    if(!title) title = promo.TransId ? promo.TransId : '';
    if (typeof title == 'string')
      title = title.toLowerCase();
    return title;
  }

  getPromoName(promo) {
    return promo.PromoName || promo.code || promo.PromoCode || "";
  }

  isPromoTypeReferral(promo){
    return (promo && promo.promoType === "REFERRAL");
  }

  isRafStagedIneligible(promoEngineError): boolean {
    return !!(promoEngineError && promoEngineError.effect === PromoEngineEffect.StagedNotEligible
      && RAF_STAGED_INELIGIBLE_REGIONS.indexOf(APP_CONFIG.region.region) !== -1);
  }

  // The engine only echoes a coupon code back when it can actually apply it, which is why the code
  // appears as soon as a non-staged payment method is used and never on a staged one. Ask it the
  // same question with a non-staged method so the card can show the code without one being applied.
  // Goes straight to getPromoEnginePromotions so the page's own in-flight check is left alone.
  async getAutoApplyPromoCode(order, nonStagedPaymentMethod): Promise<string> {
    if (this.referralPromoCode)
      return this.referralPromoCode;
    if (!order || !nonStagedPaymentMethod)
      return null;
    try {
      const probe = { ...order, paymentMethod: nonStagedPaymentMethod, promoEngineCode: null, isAutoApplyPromo: false };
      const promoObj = await this.getPromoEnginePromotions(probe);
      if (promoObj && promoObj.category !== 'ERROR' && promoObj.code)
        this.referralPromoCode = promoObj.code;
      return this.referralPromoCode;
    }
    catch (ex) {
      LoggerProvider.info(this, 'Could not resolve the referral code for the ineligibility card', ex);
      return null;
    }
  }

  // The promo engine's effect is a raw backend string, shown as-is for every other rejection.
  getPromoErrorMessage(promoEngineError): string {
    if (this.isRafStagedIneligible(promoEngineError))
      return this.translate.instant('page.checkPricesPage.referralCodePaymentMethodIneligible');

    return promoEngineError ? promoEngineError.effect : '';
  }

  canShowReferralDiscount(order): boolean {
    return !!(order && order.isPromoValid && order.promoType === "REFERRAL" && order.promoAmount);
  }

  mapPromoObject(promo) : IPromotion {
    return {
      PromoId: undefined,
      TransId: undefined,
      Fee: undefined,
      IsPercentageFee: undefined,
      DigitalReferalCode: undefined,
      ExpirationDate: undefined,
      IsReferral: undefined,
      Discount: undefined,
      DiscountPerc: undefined,
      title: undefined,
      subtitle1: undefined,
      PromoCode: promo.code,
      effect: promo.effect,
      hideCode: promo.hideCode, 
      isPromoEngine: true,
      isAutoApply: promo.isAutoApply ? true: false,
      promoType: promo.type,
      promoSourcePlatform: promo.promoSourcePlatform,
      promoCode: promo.code,
      isAdvocate: promo.isAdvocate ? true: false
    };
  }
}


// WEBPACK FOOTER //
// ./src/providers/promo/promo.ts